import React, { FC, useEffect, useMemo } from "react";
import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import type { FieldValues } from "react-hook-form/dist/types/fields";
import { QuestionConditionTypeEnum } from "@green0meter/api-client";

import { StyledForm } from "./FormWrapper.styled";
import { generateValidationSchema } from "./helpers/generateValidationSchema";
import { getQueryFilteredSections } from "./helpers/getQueryFilteredSections";
import { getConditionalSections } from "./helpers/getConditionalSections";
import { getInitialFormValues } from "./helpers/getInitialFormValues";

import { useMultistepFormStore } from "@components/common/MultistepForm";
import {
  useGetCurrentCompanyInfo,
  useGetFormDraftData,
  useGetFormSections,
} from "@api";
import { useUserStore } from "@store/user";

interface FormWrapper {
  children: React.ReactNode;
  methods: UseFormReturn;
}

const FormWrapper: FC<FormWrapper> = ({ children, methods }) => {
  const {
    formSettings: { formBasics, editMode },
    sections,
    activeSection,
    searchQuery,
    searchFilter,
    setValidationSchema,
    setSections,
    setFilteredSections,
    setActiveSection,
  } = useMultistepFormStore();
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const { formSections } = useGetFormSections(user, formBasics.id);
  const { formDraftData, isSuccessFormDraftData } = useGetFormDraftData(
    user,
    formBasics.id,
    !editMode,
  );
  const { t } = useTranslation();
  const { handleSubmit, reset, getValues } = methods;

  const namesToWatch = useMemo(() => {
    if (!formSections || isEmpty(formSections)) return [] as string[];

    const set = new Set<string>();

    for (const section of formSections) {
      for (const q of section.formQuestions ?? []) {
        for (const cond of q.conditions ?? []) {
          if (cond?.type === QuestionConditionTypeEnum.QUESTION) {
            set.add(cond.variable);
          }
        }

        if (q.questionConditionalModals?.length) {
          set.add(q.code);
        }

        if (q.isRequired) {
          set.add(q.code);
        }
      }
    }

    return Array.from(set);
  }, [formSections]);

  const initialValues = useMemo(
    () => getInitialFormValues(formDraftData),
    [formDraftData],
  );

  const defaultWatchedArray = useMemo(
    () => namesToWatch.map((n) => initialValues[n]),
    [namesToWatch, initialValues],
  );

  const watchedArray = useWatch({
    control: methods.control,
    name: namesToWatch,
    defaultValue: defaultWatchedArray,
  });

  const watchedValues = useMemo(() => {
    if (!namesToWatch.length) return {} as Record<string, unknown>;

    return Object.fromEntries(
      namesToWatch.map((n, i) => [
        n,
        watchedArray?.[i] ?? initialValues[n] ?? getValues(n),
      ]),
    );
  }, [watchedArray, namesToWatch, initialValues, getValues]);

  const { enqueueSnackbar } = useSnackbar();

  const onSubmitValid = (): void => {
    // enqueueSnackbar(t("common.form.formSubmitted"), { variant: "success" });
  };

  const onSubmitInvalid = (): void => {
    enqueueSnackbar(t("common.form.fillInRequiredFields"), { variant: "warning" });
  };

  // Validation schema
  useEffect(() => {
    setValidationSchema(generateValidationSchema(sections));
  }, [sections]);

  // Set form values from draft data
  useEffect(() => {
    const values: FieldValues = getInitialFormValues(formDraftData);

    reset(values);
  }, [formBasics.id, isSuccessFormDraftData]);

  const conditionalSectionsMemo = useMemo(() => {
    if (!formSections || (!formDraftData && !editMode)) return [];

    const values = watchedValues || {};

    return getConditionalSections(
      formSections,
      values,
      editMode,
      currentCompanyInfo,
    );
  }, [formSections, formDraftData, editMode, watchedValues]);

  useEffect(() => {
    if (!conditionalSectionsMemo) return;

    setSections(conditionalSectionsMemo);

    const currentActiveSection = conditionalSectionsMemo
      .filter((section) => section.isVisible)
      .find((section) => section.id === activeSection?.id);

    if (currentActiveSection) {
      setActiveSection(currentActiveSection);
    } else if (!isEmpty(conditionalSectionsMemo)) {
      setActiveSection(
        conditionalSectionsMemo.filter((section) => section.isVisible)[0],
      );
    } else {
      setActiveSection(undefined);
    }
  }, [conditionalSectionsMemo, activeSection, setSections, setActiveSection]);

  const queryFilteredSectionsMemo = useMemo(() => {
    if (searchQuery) {
      return getQueryFilteredSections(sections, searchQuery, searchFilter);
    }

    return sections || [];
  }, [sections, searchQuery, searchFilter]);

  useEffect(() => {
    setFilteredSections(queryFilteredSectionsMemo);
  }, [queryFilteredSectionsMemo, setFilteredSections]);

  return (
    <FormProvider {...methods}>
      <StyledForm
        onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
        autoComplete="off"
      >
        {children}
      </StyledForm>
    </FormProvider>
  );
};

export default FormWrapper;
